import { useState } from "react";
import { formatDate, formatPrayerForShare } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { MoreHorizontal, Copy, Pencil, Trash2, CheckCircle2, HandHeart, RotateCcw } from "lucide-react";

export default function PrayerCard({ prayer, onEdit, onDelete, onPrayed, onAnswered, onRestore, index = 0 }) {
  const [loading, setLoading] = useState(null);

  const withLoading = (key, fn) => async () => {
    setLoading(key);
    try {
      await fn();
    } finally {
      setLoading(null);
    }
  };

  const handleCopy = async () => {
    const text = formatPrayerForShare(prayer);
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Prayer copied to clipboard");
    } catch {
      toast.error("Couldn't copy — please try again");
    }
  };

  const prayedCount = prayer.prayed_dates?.length || 0;
  const lastPrayed = prayedCount ? prayer.prayed_dates[prayedCount - 1] : null;
  const isSameDay = (iso) => {
    if (!iso) return false;
    const d = new Date(iso);
    const now = new Date();
    return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
  };
  const prayedToday = isSameDay(lastPrayed);

  const handlePrayedClick = async () => {
    if (prayedToday) {
      toast("You've already prayed for this today", {
        description: "Rest in that. Come back tomorrow — or add another if it's on your heart.",
        action: {
          label: "Pray again",
          onClick: () => onPrayed && onPrayed(),
        },
        duration: 5000,
      });
      return;
    }
    if (onPrayed) await onPrayed();
  };

  return (
    <article
      className="prayer-card p-6 md:p-7 flex flex-col animate-fadeUp"
      style={{ animationDelay: `${Math.min(index * 60, 400)}ms` }}
      data-testid={`prayer-card-${prayer.id}`}
    >
      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#75737A]">
            {formatDate(prayer.created_at)}
          </div>
          <h3 className="mt-1.5 text-xl md:text-[22px] leading-snug text-[#2C2A2E] break-words">
            {prayer.title}
          </h3>
          {prayer.for_whom && (
            <div className="mt-1 text-sm text-[#9B8BAF] italic">for {prayer.for_whom}</div>
          )}
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="rounded-full p-1.5 hover:bg-[#EFEBF4] transition-colors"
              data-testid={`prayer-menu-${prayer.id}`}
              aria-label="Prayer options"
            >
              <MoreHorizontal className="h-4 w-4 text-[#75737A]" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-white border-[#D4C7E1]">
            {!prayer.is_answered && (
              <DropdownMenuItem onClick={onEdit} data-testid={`edit-prayer-${prayer.id}`}>
                <Pencil className="h-4 w-4 mr-2" /> Edit
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={handleCopy} data-testid={`copy-prayer-${prayer.id}`}>
              <Copy className="h-4 w-4 mr-2" /> Copy to share
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={onDelete}
              className="text-[#C28A8A]"
              data-testid={`delete-prayer-${prayer.id}`}
            >
              <Trash2 className="h-4 w-4 mr-2" /> Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </header>

      {prayer.details && (
        <p className="mt-4 text-sm leading-relaxed text-[#3F3D42] whitespace-pre-wrap">
          {prayer.details}
        </p>
      )}

      {prayer.categories?.length > 0 && (
        <div className="mt-5 flex flex-wrap gap-2">
          {prayer.categories.map((c) => (
            <span key={c} className="chip" data-testid={`prayer-category-${prayer.id}-${c}`}>{c}</span>
          ))}
        </div>
      )}

      <footer className="mt-6 pt-5 border-t border-[#EFEBF4] flex items-center justify-between gap-3">
        <div className="text-xs text-[#75737A] min-w-0">
          {prayer.is_answered ? (
            <div className="flex items-center gap-1.5 text-[#92A796]">
              <CheckCircle2 className="h-3.5 w-3.5" />
              Answered {formatDate(prayer.answered_at)}
            </div>
          ) : prayedCount ? (
            <span>
              Prayed <b className="text-[#2C2A2E]">{prayedCount}×</b> · last {formatDate(lastPrayed)}
            </span>
          ) : (
            <span>Not yet prayed for</span>
          )}
        </div>

        {prayer.is_answered ? (
          <Button
            size="sm"
            variant="outline"
            className="rounded-full border-[#D4C7E1] text-[#2C2A2E] hover:bg-[#EFEBF4] h-8"
            onClick={withLoading("restore", onRestore)}
            disabled={loading === "restore"}
            data-testid={`restore-prayer-${prayer.id}`}
          >
            <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Restore
          </Button>
        ) : (
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              className="rounded-full border-[#D4C7E1] text-[#2C2A2E] hover:bg-[#EFEBF4] h-8"
              onClick={withLoading("prayed", handlePrayedClick)}
              disabled={loading === "prayed"}
              data-testid={`mark-prayed-${prayer.id}`}
            >
              <HandHeart className="h-3.5 w-3.5 mr-1.5" />
              {prayedToday ? "Prayed today" : "Prayed"}
            </Button>
            <Button
              size="sm"
              className="btn-primary rounded-full h-8 px-3.5"
              onClick={withLoading("answered", onAnswered)}
              disabled={loading === "answered"}
              data-testid={`mark-answered-${prayer.id}`}
            >
              <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" /> Answered
            </Button>
          </div>
        )}
      </footer>
    </article>
  );
}
